<template>
  <LayoutOverlay
    v-show="!isDrawPointActive"
    class="modal-edit-poi"
    @closeOverlay="closeModal(true)"
  >
    <div class="container">
      <h2>
        {{
          t('general__data__create') +
          '/' +
          t('general__text__edit').toLocaleLowerCase() +
          ' ' +
          t('general__text__poi').toLocaleLowerCase()
        }}
      </h2>
      <div class="content">
        <TextFieldStandard
          :error="!name"
          :error-message="'* ' + t('general__text__required-field')"
          :hideIconX="true"
          :label="t('general__text__name') + ' *'"
          :prefilledText="namePrefilledText"
          @emitInput="updateName"
        />
        <label class="label" for="category">{{ t('general__text__category') + ' *' }}</label>
        <div class="category" id="category">
          <DropdownStandard
            class="dropdown"
            :disabled="false"
            :hasSearch="true"
            :title="title"
            :searchType="t('general__text__category').toLocaleLowerCase()"
            :options="dropdownCats"
            @select="onSelectedCat"
          />
          <DropdownStandard
            :disabled="!dropdownSubCats.length"
            :hasSearch="true"
            :title="subtitle"
            :searchType="t('general__text__sub-category').toLocaleLowerCase()"
            :options="dropdownSubCats"
            @select="setSubCategory"
          />
        </div>
        <label class="label" for="description">{{ t('general__text__description') }}</label>
        <textarea
          id="description"
          v-model="desc"
          :placeholder="descriptionPlaceholder()"
          class="textarea"
        ></textarea>

        <div class="height">
          <TextFieldStandard
            :error="!rh2000String.length"
            :error-message="
              rh2000StringValid
                ? '* ' + t('general__text__required-field')
                : '* ' + t('general__text__enter-a-number')
            "
            :infoText="
              isOutdoorPoi
                ? t('info__poi-height-rh2000-realestate')
                : t('info__poi-height-rh2000-level')
            "
            :label="t('general__text__height-in-RH')"
            :hideIconX="true"
            :prefilledText="rh2000PrefilledText ? rh2000PrefilledText : ''"
            @emitInput="updateRH2000String"
          />
          <TextFieldStandard
            :error="!estimatedHeightValid"
            :error-message="!estimatedHeightValid ? '* ' + t('general__text__enter-a-number') : ''"
            :label="
              isPlanviewShowing
                ? t('general__text__estimated-height-from-floor-m')
                : t('general__text__estimated-height-from-ground-m')
            "
            :infoText="t('info__poi-estimated-height')"
            :hideIconX="true"
            :prefilledText="
              estimatedHeight || estimatedHeight === 0 ? formatNumber(estimatedHeight, 2) : ''
            "
            @emitInput="updateEstimatedHeight"
          />
        </div>
      </div>
      <div class="footer">
        <ButtonOutline
          v-if="editMode"
          class="button"
          :buttonText="t('general__text__move')"
          size="small"
          @onClicked="handleMoveButtonClick"
        ></ButtonOutline>
        <ButtonOutline
          class="button"
          :buttonText="t('general__text__cancel')"
          size="small"
          @onClicked="closeModal(true)"
        ></ButtonOutline>
        <ButtonPrimary
          class="button"
          :buttonText="t('general__data__save')"
          size="small"
          @onClicked="savePoi()"
        ></ButtonPrimary>
      </div>
    </div>
  </LayoutOverlay>
</template>
<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import { getPoiById, updatePoi, createPoi, getPoiCategoriesForRealm } from '@/services/pois.service'
import { formatNumber } from '@/utils/math'
import { orderAlphabetical } from '@/utils/sort'
import { PoiCategoryDto, RequestPoi, POI, TYPE, POI_CATEGORY } from '@/interfaces/poi.interface'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import DropdownStandard, { optionType } from '@/design-system/molecules/DropdownStandard.vue'
import TextFieldStandard from '@/design-system/atoms/TextFieldStandard.vue'
import LayoutOverlay from '@/design-system/templates/LayoutOverlay.vue'
import confetti from 'canvas-confetti'
// emits
interface EMITS {
  (e: 'closeEditPoiModal')
}
const emits = defineEmits<EMITS>()

// variables (almost pure store)
const store = useStore()
const piniaStore = useGeneralStore()
const { t } = useI18n()
const rh2000String = ref<string>('')
const rh2000StringValid = ref<boolean>(true)
const dropdownSubCats = ref<optionType[]>([])
const dropdownCats = ref<optionType[]>([])
const drawPointFeature = computed(() => store.state.drawPointFeature)
const isDrawPointActive = computed<boolean>(() => store.getters.isDrawPointActive)
const isPlanviewShowing = computed<boolean>(() => store.getters.isPlanviewShowing)
const outdoorPoisForSelectedRealEstate = computed(
  () => store.state.mapview.outdoorPoisForSelectedRealEstate
)
const isBuildingSelected = computed(
  () => Object.keys(store.state.mapview.selectedBuilding).length > 0
)
const isOutdoorPoi = computed(() => {
  return !isBuildingSelected.value
})
const navvisLinks = computed(() => {
  if (isOutdoorPoi.value) {
    return selectedRealEstate.value?.navvisLinks || []
  }
  return store.state.mapview.selectedBuilding?.navvisLinks || []
})
const allPoiCategories = ref<PoiCategoryDto[] | undefined>([])
const poisForSelectedLevel = computed(() => store.state.planview.poisForSelectedLevel)
const rightClickCoordinate = computed(() => piniaStore.rightClickCoordinate)
const selectedLevel = computed(() => store.state.selectedLevel)
const selectedRealEstate = computed(() => store.state.mapview.selectedRealEstate)
const categoryTitle = computed(() => {
  return poiCategory.value ? poiCategory?.value.name : t('general__text__category')
})
const editMode = computed(() => {
  return (
    store.state.selectedPoi &&
    rightClickCoordinate.value[0] === 0 &&
    rightClickCoordinate.value[1] === 0
  )
})
const desc = ref<string>(editMode.value ? store.state.selectedPoi.description : '')
const editedPoi = ref<RequestPoi>({
  categoryId: editMode.value ? store.state.selectedPoi?.categoryId : '',
  coreId: editMode.value
    ? store.state.selectedPoi?.coreId
    : isOutdoorPoi.value
    ? selectedRealEstate.value.id
    : selectedLevel.value.id,
  description: editMode.value ? store.state.selectedPoi.description : '',
  elevation: editMode.value ? store.state.selectedPoi?.elevation : null,
  estimatedHeight: editMode.value ? store.state.selectedPoi?.estimatedHeight : null,
  location: {
    type: 'Point',
    coordinates: editMode.value
      ? [store.state.selectedPoi.longitude, store.state.selectedPoi.latitude]
      : [0, 0]
  },
  name: editMode.value ? store.state.selectedPoi.name : '',
  productId: editMode.value ? store.state.selectedPoi.productId : '',
  properties: {},
  typeId: editMode.value ? store.state.selectedPoi?.typeId : '',
  navVisId: editMode.value
    ? store.state.selectedPoi?.navVisId
    : isOutdoorPoi.value
    ? selectedRealEstate.value?.navVisId
    : selectedLevel.value?.navVisId,
  navVisLinks:
    navvisLinks.value?.map((linkObj, index) =>
      linkObj?.link
        ? {
            link:
              linkObj.link.charAt(linkObj.link.length - 1) === '/'
                ? linkObj.link.substring(0, linkObj.link.length - 1)
                : linkObj.link,
            order: index
          }
        : undefined
    ) || [],
  siteModelEntityId: selectedLevel.value
    ? !isOutdoorPoi.value
      ? selectedLevel.value?.navVisId
      : selectedRealEstate.value?.navVisId
    : 0
})
const estimatedHeight = ref<number | null>(
  editMode.value ? store.state.selectedPoi?.estimatedHeight : null
)
const estimatedHeightValid = ref<boolean>(true)
const name = ref<string>(editMode.value ? store.state.selectedPoi.name : '')
const namePrefilledText = computed(() => {
  if (editMode.value) return store.state.selectedPoi.name
  else return ''
})
const poiCategory = computed(() => {
  if (!editMode.value) return null
  return allPoiCategories.value
    ? allPoiCategories?.value.find((cat) => cat.id === store.state.selectedPoi?.categoryId)
    : null
})
const poiHeightRH2000 = computed<number | undefined>(() => {
  // Outdoor (real estate) POI
  if (
    editMode.value &&
    isOutdoorPoi.value &&
    typeof store.state.selectedPoi.elevation === 'number'
  ) {
    return store.state.selectedPoi.elevation
  }
  if (!editMode.value && isOutdoorPoi.value) {
    return 0
  }
  // Level POI
  if (editMode.value && store.state.selectedPoi.elevation > 0) {
    return store.state.selectedPoi.elevation
  }
  if (!editMode.value && selectedLevel.value?.location?.coordinates[2] > 0) {
    return selectedLevel.value?.location.coordinates[2] // Level coordinate height seems to be approximately the vertical center point
  }
  if (!editMode.value && selectedLevel.value?.location?.coordinates[2] === 0) {
    return 2
  }
  return null
})
const rh2000PrefilledText = computed(() => {
  if (poiHeightRH2000.value || (isOutdoorPoi.value && Number(poiHeightRH2000.value) === 0)) {
    return formatNumber(poiHeightRH2000.value, 2)
  }
  return null
})

const poiSubCategory = computed(() => {
  return poiCategory.value?.types.find((type) => type.id === store.state.selectedPoi?.typeId)
})
const subCategoryTitle = computed(() => {
  return poiSubCategory.value ? poiSubCategory.value.name : t('general__text__sub-category')
})
const subtitle = ref<string>(subCategoryTitle.value)
const title = ref<string>(categoryTitle.value)

// methods
function addPoiCategoryToStore(categoryId: string, typeId: string): POI_CATEGORY {
  const selectedCategory = allPoiCategories.value?.find((c) => c.id === categoryId)
  const existingPoiCategoriesForSelectedEntity: POI_CATEGORY[] = isOutdoorPoi.value
    ? store.state.mapview.poiCategoriesForSelectedRealEstate
    : store.state.planview.poiCategoriesForSelectedLevel
  let newCategory = selectedCategory as POI_CATEGORY
  newCategory.visible = false
  newCategory.types = newCategory.types.filter((type) => type.id === typeId)
  newCategory.types[0].visible = false
  newCategory.types[0].disabled = false
  const updatedPoiCategoriesForSelectedEntity = [...existingPoiCategoriesForSelectedEntity]
  updatedPoiCategoriesForSelectedEntity.push(newCategory)
  updatedPoiCategoriesForSelectedEntity.sort(orderAlphabetical)
  if (isOutdoorPoi.value) {
    store.commit('mapview/SET_POI_CATEGORIES_FOR_SELECTED_REAL_ESTATE', {
      categories: updatedPoiCategoriesForSelectedEntity
    })
    const filteredMapPoiLayers = [...store.state.mapview.filteredPoiLayers]
    filteredMapPoiLayers.push({
      disabled: false,
      id: newCategory.id,
      name: newCategory.name,
      visible: false,
      types: newCategory.types
    })
    store.commit('mapview/SET_FILTERED_MAP_POI_LAYERS', {
      filteredPoiLayers: filteredMapPoiLayers
    })
  } else {
    store.commit(
      'planview/SET_POI_CATEGORIES_FOR_SELECTED_LEVEL',
      updatedPoiCategoriesForSelectedEntity
    )
  }
  return newCategory
}
function closeModal(cancel?: boolean) {
  if (editMode.value && cancel) {
    // Reset poi position in store if it was moved
    const existingPoisArray = isOutdoorPoi.value
      ? [...outdoorPoisForSelectedRealEstate.value]
      : [...poisForSelectedLevel.value]
    const originalPoi = existingPoisArray.find(
      (poi) => poi.properties.guid === store.state.selectedPoi.guid
    )
    if (
      originalPoi.geometry.coordinates[0] !== originalPoi.properties.longitude ||
      originalPoi.geometry.coordinates[1] !== originalPoi.properties.latitude
    ) {
      let updatedArray: POI[] = []
      existingPoisArray.forEach((poi) => {
        if (poi.properties.guid === originalPoi.properties.guid) {
          poi.geometry.coordinates = [poi.properties.longitude, poi.properties.latitude]
        }
        updatedArray.push(poi)
      })
      if (isOutdoorPoi.value) {
        store.commit('mapview/SET_OUTDOOR_POI_DATA_FOR_REAL_ESTATE', { pois: updatedArray })
      } else {
        store.commit('planview/SET_POIS_FOR_SELECTED_LEVEL', updatedArray)
      }
    }
  }
  piniaStore.setRightClickCoordinate([0, 0])
  emits('closeEditPoiModal')
}
function descriptionPlaceholder() {
  if (editMode.value) return store.state.selectedPoi.description
  else return ''
}
function getMatchingCategoryInStore(categoryId: string) {
  const poiCategoriesForSelectedEntity: POI_CATEGORY[] = isOutdoorPoi.value
    ? store.state.mapview.poiCategoriesForSelectedRealEstate
    : store.state.planview.poiCategoriesForSelectedLevel
  const matchingCategoryInStore = poiCategoriesForSelectedEntity.find(
    (category) => category.id === categoryId
  )
  return matchingCategoryInStore
}
function handleMoveButtonClick() {
  store.commit('SET_CURRENT_TOOL', { tool: 'drawpoint' })
}
function onSelectedCat(categoryId: string): void {
  let selectedCat = allPoiCategories.value?.find((c) => c.id === categoryId)
  if (!selectedCat) {
    dropdownSubCats.value = []
    return
  }
  const types: optionType[] = selectedCat.types.map((type) => ({
    id: type.id,
    name: type.name,
    disabled: false,
    selected: (editMode.value && type.id === store.state.selectedPoi?.typeId) ?? false
  }))
  types.sort(orderAlphabetical)
  dropdownSubCats.value = types
  title.value = selectedCat.name
  if (!dropdownSubCats.value.length) {
    editedPoi.value.categoryId = categoryId
    editedPoi.value.typeId = ''
    subtitle.value = ''
  } else {
    const selectedSubCategory = dropdownSubCats.value.find((subCat) => subCat.selected)
    subtitle.value = selectedSubCategory ? selectedSubCategory.name : dropdownSubCats.value[0].name
    if (!selectedSubCategory) dropdownSubCats.value[0].selected = true
    editedPoi.value.categoryId = categoryId
    editedPoi.value.typeId = selectedSubCategory
      ? selectedSubCategory.id
      : dropdownSubCats.value[0].id
  }
}
function populateDropdownCats() {
  const poiLayers: any[] = []
  allPoiCategories.value?.forEach((cat: any) => {
    let category = { ...cat }
    category.disabled = false
    category.selected = false
    poiLayers.push(category)
  })
  dropdownCats.value = poiLayers.sort(orderAlphabetical)
}
async function savePoi() {
  if (!editMode.value && navvisLinks.value) {
    const poiType = allPoiCategories.value
      ?.flatMap((c) => c.types ?? [])
      ?.find((poiType) => poiType.id === editedPoi.value.typeId)
    if (!poiType) {
      store.commit('setInfoMessage', {
        type: 'error',
        message:
          t('general__text__category') + ' ' + t('general__text__missing').toLocaleLowerCase()
      })
      return
    }
  }
  editedPoi.value.description = desc.value
  editedPoi.value.elevation = parseFloat(rh2000String.value)
  if (editedPoi.value.location.coordinates.length < 3)
    editedPoi.value.location.coordinates.push(editedPoi.value.elevation) // Add elevation (altitude) to coordinates for Ivion sync
  editedPoi.value.name = name.value
  if (estimatedHeight.value !== null && estimatedHeight.value !== undefined)
    editedPoi.value.estimatedHeight = estimatedHeight.value
  // return
  if (editedPoi.value.name === '') {
    store.commit('setInfoMessage', {
      type: 'error',
      message: t('general__text__missing-name')
    })
    return
  }
  if (editedPoi.value.typeId === '') {
    store.commit('setInfoMessage', {
      type: 'error',
      message: t('general__text__category') + ' ' + t('general__text__missing').toLocaleLowerCase()
    })
    return
  }
  if (rh2000String.value === '') {
    store.commit('setInfoMessage', {
      type: 'error',
      message:
        t('general__text__height-in-RH') + ' ' + t('general__text__missing').toLocaleLowerCase()
    })
    return
  }
  if (!estimatedHeightValid.value) {
    store.commit('setInfoMessage', {
      type: 'error',
      message: t('general__text__enter-a-number')
    })
    return
  }
  try {
    let result
    if (!editMode.value) result = await createPoi(editedPoi.value)
    if (editMode.value) {
      delete editedPoi.value.siteModelEntityId
      result = await updatePoi(store.state.selectedPoi?.guid, editedPoi.value)
    }
    if (result) {
      store.commit('setInfoMessage', {
        type: 'success',
        message: t('general__text__poi-change-may-take-time')
      })
      const poiId = editMode.value ? store.state.selectedPoi?.guid : result.id
      const updatedPoi = await getPoiById(poiId, false)
      // Only replace level poi info in store if the selected category is already turned on, otherwise update should occur when turning it on
      if (updatedPoi !== undefined) {
        const updatedPoiCategory = setUpdatedPoiCategory(updatedPoi.categoryId, updatedPoi.typeId)
        const updatedPoiType: TYPE | undefined = updatedPoiCategory.types.find(
          (poiType) => poiType.id === updatedPoi.typeId
        )
        if (!updatedPoiType?.visible) {
          const existingPoisArray = isOutdoorPoi.value
            ? [...outdoorPoisForSelectedRealEstate.value]
            : [...poisForSelectedLevel.value]
          const poiIndex = existingPoisArray.findIndex(
            (poi) => poi.properties.guid === updatedPoi.id
          )
          if (poiIndex > -1) {
            // Remove poi from previous category/type
            const updatedArray: POI[] = []
            existingPoisArray.forEach((poi) => {
              if (poi.properties.guid !== updatedPoi.id) {
                updatedArray.push(poi)
              }
            })
            if (isOutdoorPoi.value) {
              store.commit('mapview/SET_OUTDOOR_POI_DATA_FOR_REAL_ESTATE', {
                pois: updatedArray
              })
            } else {
              store.commit('planview/SET_POIS_FOR_SELECTED_LEVEL', updatedArray)
            }
          }
          // Fetch pois for hidden type and set it to visible
          updateFilteredPoiLayers(updatedPoi.typeId, updatedPoi.categoryId, true)
        }
        if (updatedPoiType?.visible) {
          // Update poi for already visible type in store
          // Would it be a better solution to remember which types are already visible and "reset" visibility to trigger new poi get requests for the level?
          const existingPoisArray = isOutdoorPoi.value
            ? [...outdoorPoisForSelectedRealEstate.value]
            : [...poisForSelectedLevel.value]
          let updatedArray: POI[] = []
          if (editMode.value) {
            existingPoisArray.forEach((poi) => {
              if (poi.properties.guid === updatedPoi.id) {
                poi.geometry.coordinates = [updatedPoi.longitude, updatedPoi.latitude]
                poi.properties.categoryId = updatedPoi.categoryId
                poi.properties.color = updatedPoiType?.color
                poi.properties.description = updatedPoi.description
                poi.properties.elevation = updatedPoi.elevation
                poi.properties.estimatedHeight = updatedPoi.estimatedHeight
                poi.properties.latitude = updatedPoi.latitude
                poi.properties.longitude = updatedPoi.longitude
                poi.properties.name = updatedPoi.name
                poi.properties.typeId = updatedPoi.typeId
              }
              updatedArray.push(poi)
            })
          } else {
            updatedArray = existingPoisArray
            const newPoi: POI = {
              id: updatedPoi.id!,
              geometry: {
                type: 'Point',
                coordinates: [updatedPoi.longitude, updatedPoi.latitude]
              },
              type: 'Feature',
              properties: updatedPoi
            }
            newPoi.properties.color = updatedPoiType?.color
            updatedArray.push(newPoi)
          }
          if (isOutdoorPoi.value) {
            store.commit('mapview/SET_OUTDOOR_POI_DATA_FOR_REAL_ESTATE', { pois: updatedArray })
          } else {
            store.commit('planview/SET_POIS_FOR_SELECTED_LEVEL', updatedArray)
          }
        }
        store.dispatch('CHANGE_POI', { poi: updatedPoi })
        store.commit('SET_CLICKED_ITEM_TYPE', { type: 'poi' })
      }
    }
  } catch (error) {
    console.error(error)
    store.commit('setInfoMessage', {
      type: 'error',
      message: `${t('general__text__something-went-wrong-could-not-save-poi')}`
    })
  }

  if (store.state.oidcStore?.user?.preferred_username?.toLowerCase()?.includes('zynka')) {
    confetti({
      particleCount: 400,
      spread: 300,
      origin: { y: 0.6 }
    })
  }
  closeModal()
}
function setSubCategory(typeId: string) {
  subtitle.value = dropdownSubCats.value.find((subCat) => subCat.id === typeId)?.name ?? ''
  editedPoi.value.typeId = typeId
}
function setUpdatedPoiCategory(categoryId: string, typeId: string) {
  const matchingPoiCategoryInStore: POI_CATEGORY | undefined =
    getMatchingCategoryInStore(categoryId)
  if (matchingPoiCategoryInStore) {
    const matchingTypeInStore: TYPE | undefined = matchingPoiCategoryInStore?.types.find(
      (type) => type.id === typeId
    )
    if (!matchingTypeInStore) {
      matchingPoiCategoryInStore.types.push(
        allPoiCategories.value?.flatMap((c) => c.types ?? [])?.find((t) => t.id === typeId) as TYPE
      )
      matchingPoiCategoryInStore.types.sort(orderAlphabetical)
    }
  }
  return matchingPoiCategoryInStore
    ? matchingPoiCategoryInStore
    : addPoiCategoryToStore(categoryId, typeId)
}
function updateEstimatedHeight(inputValue: string | number) {
  if (typeof inputValue === 'number') {
    estimatedHeight.value = inputValue
    estimatedHeightValid.value = true
    return
  }
  let reg = inputValue
  if (inputValue.includes(',')) {
    reg = inputValue.replace(',', '.')
  }
  if (inputValue.length && !Number(reg) && Number(reg) !== 0) {
    estimatedHeight.value = null
    estimatedHeightValid.value = false
    return
  }
  estimatedHeightValid.value = true
  estimatedHeight.value = parseFloat(reg)
}
function updateFilteredPoiLayers(typeId: string, categoryId: string, checked: boolean) {
  if (isOutdoorPoi.value) {
    store.dispatch('mapview/GET_OUTDOOR_POI_DATA_FOR_REAL_ESTATE', {
      categoryId: categoryId,
      typeId: typeId,
      checked: checked,
      realEstateId: store.getters['mapview/selectedRealEstateId']
    })
  } else {
    store.dispatch('planview/getPoisForLevelPerCategory', {
      categoryId: categoryId,
      typeId: typeId,
      checked: checked,
      levelId: store.getters.selectedLevelId
    })
  }
}
function updateName(val: string | number) {
  if (typeof val == 'number') {
    name.value = val.toString()
    return
  }
  name.value = val
}
function updateRH2000String(inputValue: string | number) {
  if (typeof inputValue === 'number') {
    rh2000String.value = formatNumber(inputValue, 5)
    rh2000StringValid.value = true
    return
  }
  let reg = inputValue
  if (inputValue.includes(',')) {
    reg = inputValue.replace(',', '.')
  }
  if (
    (inputValue.length && !Number(reg) && !isOutdoorPoi.value) ||
    (inputValue.length && !Number(reg) && Number(reg) !== 0 && isOutdoorPoi.value)
  ) {
    rh2000String.value = ''
    rh2000StringValid.value = false
    return
  }
  rh2000StringValid.value = true
  rh2000String.value = reg
}

// watches
watch(drawPointFeature, (newPoint) => {
  if (newPoint && newPoint.geometry) {
    editedPoi.value.location = newPoint.geometry
  } else return
})
watch(
  editMode,
  (editMode) => {
    if (!editMode) {
      // Use right click coordinate for create poi
      editedPoi.value.location = { type: 'Point', coordinates: rightClickCoordinate.value }
    }
  },
  { immediate: true }
)
watch(
  rh2000PrefilledText,
  () => {
    if (typeof poiHeightRH2000.value === 'number') {
      updateRH2000String(formatNumber(poiHeightRH2000.value, 5))
    }
  },
  { immediate: true }
)
watch(dropdownSubCats, (newVal) => {
  if (newVal) dropdownSubCats.value = newVal
})
onMounted(async () => {
  allPoiCategories.value = await getPoiCategoriesForRealm(isOutdoorPoi.value ? 'OUTDOOR' : 'INDOOR')
  populateDropdownCats()
  if (editMode.value && poiCategory.value?.id) onSelectedCat(poiCategory.value.id)
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';

.container {
  background-color: variables.$color-background;
  border-radius: variables.$border-radius-small;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  left: 50%;
  max-height: 650px;
  padding: variables.$padding-xlarge;
  position: absolute;
  top: 40%;
  transform: translate(-50%, -50%);
  width: 700px;
  .category {
    display: flex;
    flex-direction: row;
    gap: variables.$gap-xlarge;
    padding-bottom: variables.$padding-medium;
    & div {
      width: 100%;
    }
  }
  .content {
    display: flex;
    flex-direction: column;
    gap: variables.$gap-small;
    justify-content: space-evenly;
    padding: variables.$padding-medium 0;
    .button {
      width: max-content;
    }
    .textarea {
      max-height: 150px;
      max-width: 668px;
      min-height: 100px;
      min-width: 100px;
      @include mixins.scrollbar-vertical;
    }
  }
  .footer {
    align-items: flex-end;
    display: flex;
    flex-direction: row;
    gap: variables.$gap-large;
    justify-content: flex-end;
    padding-top: variables.$padding-medium;

    .button {
      width: 80px;
    }
  }
  h2 {
    margin-top: 2px;
  }
  .height {
    display: flex;
    flex-direction: row;
    gap: variables.$gap-xlarge;
    padding: variables.$padding-medium 0;
    & div {
      width: 100%;
    }
  }
  .label {
    @include font-mixins.desktop-bold-paragraph;
    padding: variables.$padding-small 0;
  }
}
@media screen and (max-width: 768px) {
  .container {
    display: block;
    max-height: 650px;
    @include mixins.scrollbar-vertical;
    overflow-y: auto;
    top: 50%;
    width: 300px;
    .category {
      display: flex;
      flex-direction: column;
    }
    .content .textarea {
      max-width: 268px;
    }
  }
}
@media screen and (max-height: 670px) {
  .container {
    max-height: 500px;
  }
}
</style>
